import { Skeleton, SkeletonBadge, SkeletonText } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** What sits at the start of each row, before the text block. */
type RowLeading = 'none' | 'checkbox' | 'icon' | 'avatar' | 'tile';
/** What sits at the end. */
type RowTrailing = 'none' | 'badge' | 'button' | 'value';

interface ListRowsSkeletonProps {
    readonly rows?: number;
    readonly leading?: RowLeading;
    readonly trailing?: RowTrailing;
    /** Two-line rows carry a caption under the title. */
    readonly twoLine?: boolean;
    /**
     * `card` — each row is its own bordered box with `gap-1` between (picker
     * dialogs). `divided` — rows share hairline separators (version history).
     * `plain` — no chrome.
     */
    readonly variant?: 'card' | 'divided' | 'plain';
    readonly className?: string;
}

/** Staggered so a stack of rows doesn't read as a solid rectangle. */
const TITLE_WIDTHS = ['58%', '42%', '70%', '48%', '64%', '38%'] as const;
const CAPTION_WIDTHS = ['34%', '46%', '28%', '52%', '40%', '30%'] as const;

function RowLeadingShape({ leading }: { readonly leading: RowLeading }) {
    switch (leading) {
        case 'checkbox':
            return <Skeleton className="size-4 shrink-0 rounded-[3px]" />;
        case 'icon':
            return <Skeleton className="size-4 shrink-0 rounded-sm" />;
        case 'avatar':
            return <Skeleton className="size-8 shrink-0 rounded-full" />;
        case 'tile':
            return <Skeleton className="size-9 shrink-0 rounded-lg" />;
        case 'none':
        default:
            return null;
    }
}

function RowTrailingShape({
    trailing,
    index,
}: {
    readonly trailing: RowTrailing;
    readonly index: number;
}) {
    switch (trailing) {
        case 'badge':
            return <SkeletonBadge width={index % 2 === 0 ? 64 : 52} />;
        case 'button':
            return <Skeleton className="h-6 w-24 shrink-0 rounded-md" />;
        case 'value':
            return <SkeletonText size="sm" width="40px" />;
        case 'none':
        default:
            return null;
    }
}

/**
 * Loading placeholder for a vertical list of rows — the shape behind picker
 * dialogs, version histories, segment selectors and option lists.
 *
 * This exists because that pattern was being stood in for by a single
 * fixed-height block (`h-40`, `h-56`, `h-24`) in a dozen places, each of which
 * both misrepresented the content and mis-measured its height.
 */
export function ListRowsSkeleton({
    rows = 4,
    leading = 'none',
    trailing = 'none',
    twoLine = true,
    variant = 'card',
    className,
}: ListRowsSkeletonProps) {
    return (
        <div
            className={cn(
                'flex flex-col',
                variant === 'card' && 'gap-1',
                variant === 'divided' && 'divide-y divide-border-default',
                className,
            )}
        >
            {Array.from({ length: rows }).map((_, i) => (
                <div
                    key={i}
                    className={cn(
                        'flex items-center gap-3',
                        variant === 'card' && 'rounded-lg border border-border-default p-3',
                        variant === 'divided' && 'py-2.5',
                        variant === 'plain' && 'py-2',
                    )}
                >
                    <RowLeadingShape leading={leading} />
                    <div className="min-w-0 flex-1">
                        <SkeletonText size="sm" width={TITLE_WIDTHS[i % TITLE_WIDTHS.length]} />
                        {twoLine && (
                            <SkeletonText
                                size="xs"
                                width={CAPTION_WIDTHS[i % CAPTION_WIDTHS.length]}
                            />
                        )}
                    </div>
                    <RowTrailingShape trailing={trailing} index={i} />
                </div>
            ))}
        </div>
    );
}

interface ChipRowSkeletonProps {
    readonly count?: number;
    readonly className?: string;
}

/** Widths approximating real tag/label lengths. */
const CHIP_WIDTHS = [72, 96, 58, 110, 84, 66] as const;

/** Loading placeholder for a wrapped row of tag chips. */
export function ChipRowSkeleton({ count = 5, className }: ChipRowSkeletonProps) {
    return (
        <div className={cn('flex flex-wrap items-center gap-2', className)}>
            {Array.from({ length: count }).map((_, i) => (
                <Skeleton
                    key={i}
                    className="h-6 rounded-full"
                    style={{ width: CHIP_WIDTHS[i % CHIP_WIDTHS.length] }}
                />
            ))}
        </div>
    );
}

interface CardGridSkeletonProps {
    readonly count?: number;
    /** Grid classes of the real gallery. */
    readonly gridClassName?: string;
    /** Height of each card's body block, in px. */
    readonly cardHeight?: number;
    readonly className?: string;
}

/**
 * Loading placeholder for a gallery of selectable cards — template pickers,
 * theme pickers, plan pickers.
 */
export function CardGridSkeleton({
    count = 6,
    gridClassName = 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3',
    cardHeight = 72,
    className,
}: CardGridSkeletonProps) {
    return (
        <div className={cn(gridClassName, className)}>
            {Array.from({ length: count }).map((_, i) => (
                <div
                    key={i}
                    className="flex flex-col gap-3 rounded-xl border border-border-default bg-bg-surface p-4"
                >
                    <div className="flex items-center gap-3">
                        <Skeleton className="size-9 shrink-0 rounded-lg" />
                        <div className="min-w-0 flex-1">
                            <SkeletonText size="sm" width={TITLE_WIDTHS[i % TITLE_WIDTHS.length]} />
                            <SkeletonText
                                size="xs"
                                width={CAPTION_WIDTHS[i % CAPTION_WIDTHS.length]}
                            />
                        </div>
                    </div>
                    <Skeleton className="w-full rounded-md" style={{ height: cardHeight }} />
                </div>
            ))}
        </div>
    );
}
